import { TextField, TextFieldProps, Box, Typography } from '@mui/material';
import { forwardRef } from 'react';

/**
 * Reusable form text field component that wraps Material-UI TextField
 * with consistent styling and behavior across the application.
 *
 * Features:
 * - Consistent sizing and spacing
 * - Mobile-optimized (prevents iOS auto-zoom with 16px font)
 * - Minimum touch target height (44px)
 * - Standardized error and focus states
 * - Accessibility-ready with ARIA attributes
 * - Separate label and input styling
 */
export interface FormTextFieldProps extends Omit<TextFieldProps, 'variant'> {
  /**
   * Maximum character length for input
   * @default undefined (no limit)
   */
  maxLength?: number;
}

export const FormTextField = forwardRef<HTMLInputElement, FormTextFieldProps>(
  ({ maxLength, label, required, sx, ...props }, ref) => {
    return (
      <Box sx={{ width: '100%' }}>
        {label && (
          <Typography
            component="label"
            htmlFor={props.id}
            sx={{
              display: 'block',
              fontSize: '14px',
              fontWeight: 500,
              color: props.error ? 'error.main' : 'text.primary',
              mb: 1,
            }}
          >
            {label}
            {required && (
              <Typography component="span" sx={{ color: 'error.main', ml: 0.5 }}>
                *
              </Typography>
            )}
          </Typography>
        )}
        <TextField
          ref={ref}
          variant="outlined"
          fullWidth
          InputProps={{
            ...props.InputProps,
          }}
          inputProps={{
            maxLength,
            'aria-required': required || undefined,
            ...props.inputProps,
          }}
          sx={{
            '& .MuiInputBase-root': {
              borderRadius: '10px',
              backgroundColor: 'background.paper',
              minHeight: '44px',
            },
            '& .MuiInputBase-input': {
              fontSize: { xs: '16px', sm: '14px' }, // Prevent iOS auto-zoom on mobile
              padding: '8px 12px',
              height: 'auto',
            },
            '& .MuiFormHelperText-root': {
              marginLeft: 0,
              marginTop: '6px',
              fontSize: '0.875rem',
            },
            // Default border - #E5E7EB
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#E5E7EB',
              borderWidth: '1px',
            },
            // Hover state
            '& .MuiOutlinedInput-root:hover:not(.Mui-disabled):not(.Mui-error) .MuiOutlinedInput-notchedOutline':
              {
                borderColor: 'primary.main',
              },
            // Focus state - primary color border
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderWidth: 2,
              borderColor: 'primary.main',
            },
            // Error state - ensure visibility
            '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
              borderWidth: 2,
            },
            ...sx,
          }}
          {...props}
          label={undefined}
        />
      </Box>
    );
  }
);

FormTextField.displayName = 'FormTextField';
